import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../api/axiosDefaults";
import TransactionsTable from "./TransactionTable";
import styles from "../../styles/Account.module.css";
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import Pagination from "react-bootstrap/Pagination"

/** Server page size for /payments-latest/ (verified: count 165, 10 results per page). */
const PAGE_SIZE = 10;

const headers = [
  "Plan Name",
  "Payment Method",
  "Card No.",
  "Amount",
  "Status",
  "Payment Date",
];

const getStatus = (status) => {
  switch (status) {
    case "succeeded":
      return "Success";
    case "pending":
      return "Pending";
    case "failed":
      return "Failed";
    case "canceled":
      return "Canceled";

    default:
      return "";
  }
};

/**
 * Takes the "next/previous page" link returned by the server and makes it usable for our app.
 *
 * - The server sometimes returns a full link (example: `https://some-domain.com/.../?page=2`).
 * - We only want the "inside the app" part (example: `/.../?page=2`).
 * - That way, clicking Next/Prev works the same in local/dev/prod without worrying about domains.
 */
const toRelativeApiUrl = (maybeAbsoluteUrl) => {
  if (!maybeAbsoluteUrl) return null;
  try {
    const parsed = new URL(maybeAbsoluteUrl, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (e) {
    return maybeAbsoluteUrl;
  }
};

/**
 * Figures out what page number a pagination link is pointing to.
 *
 * - When you click Next/Prev, we fetch using the link the server gave us.
 * - But we also need to update the UI (highlight the active page number).
 * - This reads `page=...` from the link so the UI knows "you're on page X".
 * - If the link does not have `page=`, we treat it as page 1 (some APIs omit `?page=1`).
 */
const parsePageFromUrl = (pageUrl) => {
  if (!pageUrl) return null;
  try {
    const parsed = new URL(pageUrl, window.location.origin);
    const value = parsed.searchParams.get("page");
    // Many paginated APIs omit `?page=1` for the first page.
    if (value == null || value === "") return 1;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch (e) {
    return null;
  }
};

/**
 * Builds the list of pagination "slots" to render.
 *
 * What it's for (Pagination):
 * - Keeps the pagination bar compact on large result sets by showing:
 *   - first page, last page
 *   - current page and its immediate neighbors
 *   - ellipses where page ranges are skipped
 *
 * Output format:
 * - Numbers represent clickable page buttons.
 * - The string `"ellipsis"` represents a non-clickable gap indicator.
 */
const buildPaginationItems = ({ currentPage, totalPages }) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const clamped = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const items = [];
  for (let i = 0; i < clamped.length; i++) {
    const value = clamped[i];
    const prev = clamped[i - 1];
    if (i > 0 && value - prev > 1) items.push("ellipsis");
    items.push(value);
  }
  return items;
};

const getWrappedErrorMessage = (wrapped, fallback) => {
  const data = wrapped?.error?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    const parts = data.detail.map((item) =>
      typeof item === "string" ? item : item?.msg || JSON.stringify(item)
    );
    if (parts.length) return parts.join(" ");
  }
  if (typeof data?.message === "string") return data.message;
  return fallback;
};

const isAuthError = (wrapped) => {
  const status = wrapped?.error?.response?.status;
  return status === 401 || status === 440;
};

export default function TransactionsPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);
  const [pageNavLoading, setPageNavLoading] = useState(false);

  const fetchData = async ({ url, page } = {}) => {
    try {
      setLoading(true);
      const params = {};
      if (!url && page) params.page = page;

      const response = await axiosInstance.get(url || `/payments-latest/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params: url ? undefined : params,
        withCredentials: true,
      });

      // The interceptor resolves (never rejects) on error, so catch won't fire here.
      if (response?.success === false) {
        if (!isAuthError(response)) {
          toast.error(getWrappedErrorMessage(response, "Unable to load transactions."));
        }
        return;
      }

      const payload = response?.data?.data;
      setCount(payload?.count ?? 0);
      setNextUrl(toRelativeApiUrl(payload?.next) ?? null);
      setPrevUrl(toRelativeApiUrl(payload?.previous) ?? null);

      if (!url) {
        setCurrentPage(page || 1);
      } else {
        const pageFromUrl = parsePageFromUrl(url);
        if (pageFromUrl != null) setCurrentPage(pageFromUrl);
      }

      const results = payload?.results;
      const modifiedData =
        results?.length > 0
          ? results.map((el) => ({
              plan_name: el?.plan_name,
              payment_method_type: el?.payment_method_type,
              card_last_four: el?.card_last_four,
              amount: el?.amount,
              status: getStatus(el?.status),
              created_at: el?.created_at,
            }))
          : [];

      setData(modifiedData);
    } catch (err) {
      console.log(err);
      toast.error("Unable to load transactions.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigates to a new page using a server-provided pagination URL (`next`/`previous`).
   * On arrow click.
   */
  const handleNavigate = async (url) => {
    if (!url || pageNavLoading) return;
    setPageNavLoading(true);
    try {
      await fetchData({ url });
    } finally {
      setPageNavLoading(false);
    }
  };

  /**
   * Navigates to a specific page number by requesting `?page=N`.
   * On number click.
   */
  const handleNavigatePage = async (page) => {
    if (!page || pageNavLoading || page === currentPage) return;
    setPageNavLoading(true);
    try {
      await fetchData({ page });
    } finally {
      setPageNavLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const pageItems = buildPaginationItems({ currentPage, totalPages });
  const showPagination = totalPages > 1;

  return (
    <div>
      {/* <h2
          className="text-center py-4 w-100 mt-2"
            style={{
              background: "#3B444A",
              color: "#F5F5F5",
              fontFamily: "Playfair Display",
              textTransform: "uppercase",
            }}
          >
            Transaction List
          </h2> */}
          <div className={`${styles.Top}`}>
    <Row className="py-3 text-center">
      <Col className=" d-flex align-items-center justify-content-center">
      <h3
      style={{
              textTransform: "uppercase",
              // fontFamily: "Playfair Display",
            }}
      className={` ${styles.TopName2} pt-3`}>
          Transaction List
        </h3>
      </Col>
    </Row>
    </div>
      <div
      className="py-4"
      style={{
        margin: "0 auto",
        maxWidth: "1400px",
        overflowX:"auto"
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.6rem",
        }}
      >
        <TransactionsTable
          headers={headers}
          data={data}
          dataLoading={loading && data.length === 0}
        />

        {showPagination && (
          <div
            className={`d-flex justify-content-center align-items-center flex-wrap mt-3 ${styles.paginationRoot}`}
          >
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={() => handleNavigate(prevUrl)}
                disabled={prevUrl == null || pageNavLoading}
              />
              {pageItems.map((item, idx) =>
                item === "ellipsis" ? (
                  <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                ) : (
                  <Pagination.Item
                    key={`page-${item}`}
                    active={item === currentPage}
                    disabled={pageNavLoading}
                    onClick={() => handleNavigatePage(item)}
                  >
                    {item}
                  </Pagination.Item>
                )
              )}
              <Pagination.Next
                onClick={() => handleNavigate(nextUrl)}
                disabled={nextUrl == null || pageNavLoading}
              />
            </Pagination>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
